/**
 * The shell: the minimal frame + navigation between the two S0 views (login/lobby and session). It
 * owns the {@link Terminal}, renders the active view's lines, and routes the input bar to that view's
 * command handler. It holds no domain state — it renders from {@link AppApi} and forwards intents.
 *
 * (Follow-up: this is NOT the full 7-view tui-layout — only the S0 lobby + session loop.)
 */
import { Terminal } from '../term/index.ts';
import { loginCommand, renderLogin } from '../views/loginView.ts';
import {
  renderSession,
  renderSessionSide,
  sessionChrome,
  sessionCommand,
  sessionCompletion,
} from '../views/sessionView.ts';
import { loginCompletion } from '../views/loginView.ts';
import type { AppApi, ViewName } from './appApi.ts';

const PROMPTS: Record<ViewName, string> = {
  login: 'lobby>',
  session: 'say>',
};

export class Shell {
  private readonly term: Terminal;
  private view: ViewName = 'login';

  constructor(
    mount: HTMLElement,
    private readonly api: AppApi,
  ) {
    this.term = new Terminal(mount);
    // Completion asks the ACTIVE view, so the same Tab means different things in the lobby and in a
    // session - which is the only way it can complete ids the session actually holds.
    this.term.onComplete((text, caret) =>
      this.view === 'login'
        ? loginCompletion(this.api, text, caret)
        : sessionCompletion(this.api, text, caret),
    );
    this.term.onSubmit((text) => this.onInput(text));
    this.term.setPrompt(PROMPTS[this.view]);
  }

  setView(view: ViewName): void {
    this.view = view;
    this.term.setPrompt(PROMPTS[view]);
    this.render();
    this.term.focus();
  }

  currentView(): ViewName {
    return this.view;
  }

  render(): void {
    if (this.view === 'login') {
      // The lobby has no session to be oriented in, so it carries no chrome.
      this.term.renderChrome(null);
      this.term.render(renderLogin(this.api));
      return;
    }

    this.term.renderChrome(sessionChrome(this.api));
    // Two columns: the active panel, and the chat rail beside it (empty rail = one column).
    this.term.render(renderSession(this.api), renderSessionSide(this.api));
  }

  focus(): void {
    this.term.focus();
  }

  private onInput(text: string): void {
    if (this.view === 'login') loginCommand(this.api, text);
    else sessionCommand(this.api, text);
  }
}
